// Regras de apresentação da lista de processadores — testáveis sem render.
import type { MerchantProcessor } from "@/types/admin-merchant-processor"
import type { ProcessorCertificateStatus } from "@/types/admin-processor-config"

/**
 * Urgência do certificado. Ordena a lista: o que precisa de ação não pode ficar
 * na terceira dobra, e uma credencial vencida some no meio de dez válidas.
 */
const URGENCY: Record<ProcessorCertificateStatus, number> =
  {
    EXPIRED: 0,
    EXPIRING: 1,
    NOT_DEFINED: 2,
    VALID: 3,
    NOT_APPLICABLE: 3,
  }

const urgencyOf = (processor: MerchantProcessor): number =>
  processor.certificate
    ? (URGENCY[processor.certificate.status] ?? 3)
    : 3

/** Prazo só importa perto do fim: um certificado de dois anos não merece pixel. */
export const DAYS_TO_SHOW_EXPIRY = 60

export const shouldShowExpiry = (
  processor: MerchantProcessor
): boolean => {
  const days = processor.certificate?.daysToExpire
  return (
    typeof days === "number" && days <= DAYS_TO_SHOW_EXPIRY
  )
}

/** Certificado que já venceu ou vence em breve — o que justifica abrir o grupo. */
export const needsAttention = (
  processor: MerchantProcessor
): boolean => {
  const status = processor.certificate?.status
  return status === "EXPIRED" || status === "EXPIRING"
}

/** Credencial que nada usa — a única que pode ser apagada. */
export const isUnused = (
  processor: MerchantProcessor
): boolean => processor.linkedConfigsCount === 0

export interface ProcessorFilters {
  search: string
  gateway: string | null
}

export const EMPTY_PROCESSOR_FILTERS: ProcessorFilters = {
  search: "",
  gateway: null,
}

/** Busca pelo NOME, que é como o operador se refere à credencial. */
export const filterProcessors = (
  processors: MerchantProcessor[],
  filters: ProcessorFilters
): MerchantProcessor[] => {
  const search = filters.search.trim().toLowerCase()
  return processors.filter((processor) => {
    if (
      filters.gateway &&
      processor.gateway !== filters.gateway
    ) {
      return false
    }
    if (!search) return true
    return (processor.name ?? processor.gateway)
      .toLowerCase()
      .includes(search)
  })
}

export interface ProcessorGroup {
  gateway: string
  processors: MerchantProcessor[]
}

/**
 * Quais grupos abrem sozinhos.
 *
 * O grupo colapsado existe porque um merchant com oito credenciais Itaú vira uma parede — mas
 * colapsar tudo esconderia justamente o certificado vencido que o operador veio resolver. Abrem
 * os que têm algo a resolver; se nenhum tiver, abre o primeiro, para a tela nunca começar
 * inteiramente fechada.
 */
export const groupsOpenByDefault = (
  groups: ProcessorGroup[]
): string[] => {
  const urgent = groups
    .filter((group) =>
      group.processors.some(needsAttention)
    )
    .map((group) => group.gateway)
  if (urgent.length > 0) return urgent
  return groups.length > 0 ? [groups[0].gateway] : []
}

/**
 * Agrupa por banco e ordena por urgência do certificado, depois por nome.
 *
 * O agrupamento existe por causa do caso real: um merchant com oito credenciais
 * Itaú vira uma parede de linhas idênticas, e o nome do banco repetido em cada
 * uma não distingue nenhuma delas.
 *
 * Os grupos vêm na ordem da credencial mais urgente de cada um — a agência com
 * certificado vencido não pode ficar embaixo de uma saudável.
 */
export const groupProcessors = (
  processors: MerchantProcessor[]
): ProcessorGroup[] => {
  const byGateway = new Map<string, MerchantProcessor[]>()
  for (const processor of processors) {
    const current = byGateway.get(processor.gateway) ?? []
    current.push(processor)
    byGateway.set(processor.gateway, current)
  }

  const compare = (
    a: MerchantProcessor,
    b: MerchantProcessor
  ) =>
    urgencyOf(a) - urgencyOf(b) ||
    (a.name ?? "").localeCompare(b.name ?? "")

  return [...byGateway.entries()]
    .map(([gateway, items]) => ({
      gateway,
      processors: [...items].sort(compare),
    }))
    .sort(
      (a, b) =>
        urgencyOf(a.processors[0]) -
          urgencyOf(b.processors[0]) ||
        a.gateway.localeCompare(b.gateway)
    )
}

/** Quantas credenciais pedem atenção — alimenta o banner do topo. */
export const countExpiringProcessors = (
  processors: MerchantProcessor[]
): number =>
  processors.filter(
    (processor) =>
      processor.certificate?.status === "EXPIRING" ||
      processor.certificate?.status === "EXPIRED"
  ).length
